import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { updateChannelMember } from 'flow/actions/channels-actions';
import { updateProfile } from 'flow/actions/profile-actions';
import SettingRow from 'flow/components/Setting/SettingRow';
import SettingSection from 'flow/components/Setting/SettingSection';
import Toggle from 'flow/components/ui-components/Toggle';
import useAppContext from 'flow/hooks/useAppContext';
import useChannel from 'flow/hooks/useChannel';
import useExtension from 'flow/hooks/useExtension';
import useProfiles from 'flow/hooks/useProfiles';
import { ChannelKey, ExtensionKey, ProfileKey } from 'flow/types/EntityKeys';
import { ChannelLoginList } from 'flow/types/Profile';
import { TypedMap } from 'flow/types/TypedMap';
import { fromJS } from 'immutable';
import React from 'react';
import { useParams } from 'react-router-dom';
import { selectExtensionActiveProfileKey } from 'flow/selectors/ExtensionSelectors';

const LiveChats = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { extensionKey, profileKey } = useParams<{
        extensionKey: ExtensionKey;
        profileKey: ProfileKey;
    }>();

    const { channels, channelNames } = useChannel(extensionKey);
    const { extension } = useExtension(extensionKey);
    const { profiles, getProfile } = useProfiles(extensionKey);
    const activeProfileKey = useAppSelector((state) =>
        selectExtensionActiveProfileKey(state, extensionKey),
    );

    const myChannelLogins = profiles.first().get('channelLoginList');

    if (!myChannelLogins?.size) {
        return null;
    }

    const channelLoginList = getProfile(profileKey).get('channelLoginList');

    const channelsLoggedIn = channelLoginList
        .filter((channelLoginList) => channelLoginList.get('loggedIn'))
        .map((channelLoginList) => channelLoginList.get('channelEntityKey'));

    const handleChange = (key: ChannelKey, value: boolean) => {
        const updatedProfileDTO = fromJS({
            key: profileKey,
            channelLoginList: updateOrCreateLoginForChannel(key, value),
        });

        dispatch(updateProfile(extensionKey, updatedProfileDTO));

        if (activeProfileKey === profileKey) {
            const memberDTO = channels
                .getIn([key, 'channelMemberDTOs'])
                .find(
                    (member) =>
                        member.get('chatUserKey') ===
                        extension.get('chatUserKey'),
                );
            const updatedMemberDTO = memberDTO.set('loggedIn', value);
            dispatch(
                updateChannelMember(
                    key,
                    updatedMemberDTO.get('key'),
                    updatedMemberDTO,
                ),
            );
        }
    };

    const updateOrCreateLoginForChannel = (
        channelEntityKey: ChannelKey,
        loggedIn: boolean,
    ) => {
        const indexOfChannelToUpdate = channelLoginList.findIndex(
            (channel) => channel.get('channelEntityKey') === channelEntityKey,
        );

        if (indexOfChannelToUpdate === -1) {
            return channelLoginList.push(
                fromJS({
                    channelEntityKey,
                    loggedIn,
                }),
            );
        }

        return channelLoginList.setIn(
            [indexOfChannelToUpdate, 'loggedIn'],
            loggedIn,
        );
    };

    return (
        <>
            <SettingSection className="text-sm/4">
                {i18next.t('settings.queue_channel_login.external_channels')}
            </SettingSection>

            {myChannelLogins
                .sort((a, b) =>
                    channelNames
                        .get(a.get('channelEntityKey'))
                        .localeCompare(
                            channelNames.get(b.get('channelEntityKey')),
                        ),
                )
                .map((channelLogin: TypedMap<ChannelLoginList>) => (
                    <SettingRow
                        key={channelLogin.get('channelEntityKey')}
                        cta={
                            <Toggle
                                value={channelsLoggedIn.includes(
                                    channelLogin.get('channelEntityKey'),
                                )}
                                onChange={(value) =>
                                    handleChange(
                                        channelLogin.get('channelEntityKey'),
                                        value,
                                    )
                                }
                                text={channelNames.get(
                                    channelLogin.get('channelEntityKey'),
                                )}
                            />
                        }
                    >
                        {channelNames.get(channelLogin.get('channelEntityKey'))}
                    </SettingRow>
                ))}
        </>
    );
};

export default LiveChats;
